// UI state management slice

import { create } from 'zustand';
import { Notification } from '../types/common';

interface UiState {
  // Language
  currentLanguage: string;
  
  // Notifications
  notifications: Notification[];
  
  // Mobile navigation
  isMobileMenuOpen: boolean;
  
  // Modals
  isWalletModalOpen: boolean;
  isPaymentModalOpen: boolean;
  
  // Theme
  isDarkMode: boolean;
}

export interface UiSlice extends UiState {
  // Actions
  setCurrentLanguage: (language: string) => void;
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  setMobileMenuOpen: (isOpen: boolean) => void;
  toggleMobileMenu: () => void;
  setWalletModalOpen: (isOpen: boolean) => void;
  setPaymentModalOpen: (isOpen: boolean) => void;
  setDarkMode: (isDarkMode: boolean) => void;
  toggleDarkMode: () => void;
}

// Create UI slice
export const uiSlice = (set: any, get: any): UiSlice => ({
  // Initial state
  currentLanguage: 'en',
  notifications: [],
  isMobileMenuOpen: false,
  isWalletModalOpen: false,
  isPaymentModalOpen: false,
  isDarkMode: true,

  // Set current language
  setCurrentLanguage: (language: string) => {
    set({ currentLanguage: language });
    // Save to localStorage
    localStorage.setItem('idngold_language', language);
  },

  // Add notification
  addNotification: (notification: Omit<Notification, 'id'>) => {
    const id = Date.now().toString();
    const newNotification: Notification = {
      ...notification,
      id,
    };
    
    set((state: UiSlice) => ({
      notifications: [...state.notifications, newNotification],
    }));
    
    // Auto-remove notification after duration (default: 5 seconds)
    const duration = notification.duration || 5000;
    setTimeout(() => {
      get().removeNotification(id);
    }, duration);
  },

  // Remove notification
  removeNotification: (id: string) => {
    set((state: UiSlice) => ({
      notifications: state.notifications.filter((n: Notification) => n.id !== id),
    }));
  },

  // Clear all notifications
  clearNotifications: () => {
    set({ notifications: [] });
  },

  // Set mobile menu open state
  setMobileMenuOpen: (isOpen: boolean) => {
    set({ isMobileMenuOpen: isOpen });
  },

  // Toggle mobile menu
  toggleMobileMenu: () => {
    set((state: UiSlice) => ({ isMobileMenuOpen: !state.isMobileMenuOpen }));
  },

  // Set wallet modal open state
  setWalletModalOpen: (isOpen: boolean) => {
    set({ isWalletModalOpen: isOpen });
  },

  // Set payment modal open state
  setPaymentModalOpen: (isOpen: boolean) => {
    set({ isPaymentModalOpen: isOpen });
  },

  // Set dark mode
  setDarkMode: (isDarkMode: boolean) => {
    set({ isDarkMode });
    // Save to localStorage
    localStorage.setItem('idngold_theme', isDarkMode ? 'dark' : 'light');
  },

  // Toggle dark mode
  toggleDarkMode: () => {
    set((state: UiSlice) => {
      const newDarkMode = !state.isDarkMode;
      localStorage.setItem('idngold_theme', newDarkMode ? 'dark' : 'light');
      return { isDarkMode: newDarkMode };
    });
  },
});

// Export a hook to use only the UI slice
export const useUiStore = create<UiSlice>((set, get) => ({
  ...uiSlice(set, get),
}));